/**
 * Created by katarinababic on 23.1.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { Modal } from 'reactstrap';
import { useTrans } from '../Store/System/LocalizationStore';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Theme } from '../Theme/Theme';
import { useNavigate } from 'react-router-dom';
import { useRootStore } from '../Store/useRootStore';
import { runInAction } from 'mobx';

const popIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const ModalContent = styled.div`
  background: linear-gradient(160deg, #ffffff 0%, #f8fffe 50%, #f0fdf9 100%);
  border-radius: 24px;
  padding: 48px 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
  animation: ${popIn} 0.4s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #00dda2, #007763, #00dda2);
    background-size: 200% 100%;
    animation: ${shimmer} 2s linear infinite;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${Theme.Colors.lightestGrey};
  color: ${Theme.Colors.charcoal};
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${Theme.Colors.lighterGrey};
    transform: scale(1.1);
  }
`;

const CelebrationEmoji = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  animation: ${float} 2s ease-in-out infinite;
`;

const PointsBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00dda2, #007763);
  color: white;
  padding: 16px 32px;
  border-radius: 50px;
  margin-bottom: 24px;
  box-shadow: 0 8px 24px rgba(0, 221, 163, 0.4);
  animation: ${pulse} 2s ease-in-out infinite;
`;

const PointsValue = styled.span`
  font-size: 32px;
  font-weight: 800;
  margin-right: 8px;
`;

const PointsLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  opacity: 0.9;
`;

const Title = styled.h2`
  color: ${Theme.Colors.textColor};
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: ${Theme.Colors.charcoal};
  font-size: 15px;
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 16px 32px;
  border-radius: 14px;
  border: none;
  background: #00dda2;
  color: white;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 16px rgba(0, 221, 163, 0.3);

  &:hover {
    background: #007763;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 112, 99, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 14px 32px;
  border-radius: 14px;
  border: none;
  background: transparent;
  color: #007763;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 221, 163, 0.1);
  }
`;

const StyledModal = styled(Modal)`
  .modal-content {
    background: transparent;
    border: none;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    border-radius: 24px;
    overflow: hidden;
  }
`;

export type PointsCollectedModalProps = {};

export const PointsCollectedModal: React.FC<PointsCollectedModalProps> = observer((props) => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { engagement } = useRootStore();

  const handleSeeMore = React.useCallback(() => {
    runInAction(() => {
      engagement.isPointsModalVisible = false;
    });
    navigate('/account');
  }, [navigate, engagement]);

  const toggle = React.useCallback(() => {
    runInAction(() => {
      engagement.isPointsModalVisible = !engagement.isPointsModalVisible;
    });
  }, [engagement]);

  return (
    <StyledModal isOpen={engagement.isPointsModalVisible} toggle={toggle} size="sm" centered>
      <ModalContent>
        <CloseButton onClick={toggle}>×</CloseButton>

        <CelebrationEmoji>🎉</CelebrationEmoji>

        <PointsBadge>
          <PointsValue>+{engagement.pointsCollected}</PointsValue>
          <PointsLabel>{t('pointsModal.healthPoints', { defaultValue: 'HP' })}</PointsLabel>
        </PointsBadge>

        <Title>{t('pointsModal.congratulations', { defaultValue: 'Congratulations!' })}</Title>
        <Subtitle>
          {t('pointsModal.earnedPoints', {
            defaultValue: "You've earned Health Points for staying active. Keep up the great work!",
          })}
        </Subtitle>

        <ButtonsContainer>
          <PrimaryButton onClick={toggle}>
            {t('pointsModal.awesome', { defaultValue: 'Awesome!' })}
          </PrimaryButton>
          <SecondaryButton onClick={handleSeeMore}>
            {t('pointsModal.seeMore', { defaultValue: 'Learn how to earn more points' })}
          </SecondaryButton>
        </ButtonsContainer>
      </ModalContent>
    </StyledModal>
  );
});
